import { useCallback, useEffect, useState } from "react"
import { useConfig } from "../../configuration/useConfig"
import { ShopperInsightsSearchHitCheckout } from "lib/types/generated/graphql-types"
import { useLazyQuery } from "@apollo/client"
import SEARCH_SHOPPER_INSIGHTS from "../../graphql/queries/shopperInsights/SearchShopperInsights"
import SEARCH_ORDERS_TOTAL_HITS from "../../graphql/queries/order/SearchOrdersTotalHits"
import esb from "elastic-builder"
import dayjs from "dayjs"
import { Query } from "lib/types/common"

interface AggregationBucket {
  key: string
  doc_count: number
  uniqueSessions: CardinalityAggregation
}

interface TermsAggregation {
  doc_count_error_upper_bound: number
  sum_other_doc_count: number
  buckets: AggregationBucket[]
}

interface FilterAggregation {
  doc_count: number
  uniqueRejectedSessions?: CardinalityAggregation
  uniqueOnHoldSessions?: CardinalityAggregation
  uniqueStartedSessions?: CardinalityAggregation
  uniquePendingSessions?: CardinalityAggregation
}

interface CardinalityAggregation {
  value: number
}

interface AllUniqueSessionsBucket {
  key: string
  doc_count: number
  latest: {
    hits: {
      hits: Array<{
        _source: {
          id?: string
          sessionId?: string
          created?: string
          orderId?: string
          orderReference?: string
          paymentStatus?: {
            current: string
            log?: Array<{ message?: string }>
          }
          providers?: { payment: { providerName: string } }
          storeGroupId?: string
          countryCode?: string
          isPendingConfirmation?: boolean
          isConfirmed?: boolean
          shippingAddress?: { email?: string }
          currencyCode?: string
          items?: Array<{
            id?: string
            quantity?: number
            totalPriceAmount?: number
          }>
        }
      }>
    }
  }
}

interface InsightsAggregations {
  allUniqueSessions?: {
    buckets: AllUniqueSessionsBucket[]
  }
  totalUniqueSessions?: CardinalityAggregation
  totalUniqueCheckoutSessions?: CardinalityAggregation
  paymentProviders?: TermsAggregation
  paymentStatuses?: TermsAggregation
  storeGroups?: TermsAggregation
  countryBreakdown?: TermsAggregation
  uniqueFailedSessions?: CardinalityAggregation
  rejectedCheckouts?: FilterAggregation
  onHoldCheckouts?: FilterAggregation
  startedCheckouts?: FilterAggregation
  pendingConfirmationCheckouts?: FilterAggregation
  rejectedSessions?: FilterAggregation
  startedSessions?: FilterAggregation
  onHoldSessions?: FilterAggregation
  pendingSessions?: FilterAggregation
}

export interface InsightsTimeSeriesData {
  timestamp: string
  displayLabel: string
  orders: number
  checkouts: number
  sessions: number
}

export function useInsightsData(params: {
  config: ReturnType<typeof useConfig>["config"]
  startDate: string
  endDate: string
  selectedStoreGroups: { value: string; label: string }[]
  selectedStoreMarkets: { value: string; label: string }[]
  selectedReasons: { value: string; label: string }[]
  numberOfHits: string
  timeZone: string
  interval?: "hourly" | "daily"
}) {
  const {
    config,
    startDate,
    endDate,
    selectedStoreGroups,
    selectedStoreMarkets,
    selectedReasons,
    numberOfHits,
    timeZone,
    interval = "daily"
  } = params

  // Aggregated data state
  const [pendingCheckouts, setPendingCheckouts] = useState<ShopperInsightsSearchHitCheckout[]>([])
  const [aggregatedData, setAggregatedData] = useState<InsightsAggregations | null>(null)
  const [ordersData, setOrdersData] = useState<{ total: number }>({ total: 0 })
  const [totalCheckoutSessions, setTotalCheckoutSessions] = useState<number>(0)

  // Time-series data state
  const [chartData, setChartData] = useState<InsightsTimeSeriesData[]>([])
  const [ordersOverTimeData, setOrdersOverTimeData] = useState<{ [key: string]: number }>({})
  const [checkoutsOverTimeData, setCheckoutsOverTimeData] = useState<{ [key: string]: number }>({})
  const [sessionsOverTimeData, setSessionsOverTimeData] = useState<{ [key: string]: number }>({})

  const formatLabel = (timestamp: string): string => {
    const date = dayjs(timestamp)
    if (interval === "hourly") {
      return date.format("MMM D, HH:mm")
    }
    return date.format("MMM D")
  }

  // State to track order session IDs
  const [orderSessionIds, setOrderSessionIds] = useState<Set<string>>(new Set())

  // Query for checkout documents (for table display)
  const [searchShopperInsights, { error, loading }] = useLazyQuery(SEARCH_SHOPPER_INSIGHTS, {
    onCompleted: (data) => {
      let uniqueCheckoutHits: ShopperInsightsSearchHitCheckout[] = []

      // Process hits directly - only fetch what we need to display
      if (data.searchShopperInsights.hits) {
        const hits = data.searchShopperInsights.hits
        const seenSessions = new Map<string, ShopperInsightsSearchHitCheckout>()

        // Deduplicate by sessionId, keeping only the latest per session
        hits.forEach((hit: ShopperInsightsSearchHitCheckout) => {
          // Only process checkout hits (not session or order hits)
          if (hit.__typename === "ShopperInsightsSearchHitCheckout" && hit.sessionId) {
            const sessionId = hit.sessionId
            const existing = seenSessions.get(sessionId)

            // Keep the one with the latest created date
            if (
              !existing ||
              new Date(hit.created || 0).getTime() > new Date(existing.created || 0).getTime()
            ) {
              seenSessions.set(sessionId, hit)
            }
          }
        })

        // Convert to array and filter out sessions with orders
        uniqueCheckoutHits = Array.from(seenSessions.values()).filter(
          (checkout: ShopperInsightsSearchHitCheckout) =>
            !orderSessionIds.has(checkout.sessionId || "")
        )
      }

      setPendingCheckouts(uniqueCheckoutHits)
    }
  })

  // Separate query for aggregations/counts only (no document data)
  const [searchCheckoutCounts, { loading: countsLoading }] = useLazyQuery(
    SEARCH_SHOPPER_INSIGHTS,
    {
      onCompleted: (data) => {
        if (data.searchShopperInsights.aggregations) {
          const parsedAggregations =
            typeof data.searchShopperInsights.aggregations === "string"
              ? JSON.parse(data.searchShopperInsights.aggregations)
              : data.searchShopperInsights.aggregations

          setAggregatedData(parsedAggregations)
        }
      }
    }
  )

  // Query to count unique order sessions by session creation time
  const [searchOrderSessions, { loading: orderSessionsLoading }] = useLazyQuery(
    SEARCH_SHOPPER_INSIGHTS,
    {
      onCompleted: (data) => {
        if (data.searchShopperInsights.aggregations) {
          const parsedAggregations =
            typeof data.searchShopperInsights.aggregations === "string"
              ? JSON.parse(data.searchShopperInsights.aggregations)
              : data.searchShopperInsights.aggregations
          setOrdersData({ total: parsedAggregations.totalOrderSessions?.value ?? 0 })

          // Extract sessionIds from order buckets
          if (parsedAggregations.orderSessionIds?.buckets) {
            const sessionIds = parsedAggregations.orderSessionIds.buckets.map(
              (bucket: { key: string }) => bucket.key
            )
            setOrderSessionIds(new Set(sessionIds))
          }
        }
      }
    }
  )

  // Query to count ALL unique checkout sessions (regardless of payment status)
  const [searchAllCheckoutSessions, { loading: allCheckoutSessionsLoading }] = useLazyQuery(
    SEARCH_SHOPPER_INSIGHTS,
    {
      onCompleted: (data) => {
        if (data.searchShopperInsights.aggregations) {
          const parsedAggregations =
            typeof data.searchShopperInsights.aggregations === "string"
              ? JSON.parse(data.searchShopperInsights.aggregations)
              : data.searchShopperInsights.aggregations
          setTotalCheckoutSessions(parsedAggregations.totalUniqueCheckoutSessions?.value ?? 0)
        }
      }
    }
  )

  // Time-series queries - Orders
  const [searchOrdersOverTime, { loading: ordersOverTimeLoading, error: ordersOverTimeError }] =
    useLazyQuery(SEARCH_ORDERS_TOTAL_HITS, {
      onCompleted: (data) => {
        if (data?.searchOrders?.aggregations) {
          const aggregations = JSON.parse(data.searchOrders.aggregations)
          const buckets = aggregations?.ordersOverTime?.buckets || []

          const ordersMap: { [key: string]: number } = {}
          buckets.forEach((bucket: { key_as_string: string; doc_count: number }) => {
            ordersMap[bucket.key_as_string] = bucket.doc_count
          })
          setOrdersOverTimeData(ordersMap)
        }
      }
    })

  // Time-series queries - Checkouts (has paymentStatus)
  const [
    searchCheckoutsOverTime,
    { loading: checkoutsOverTimeLoading, error: checkoutsOverTimeError }
  ] = useLazyQuery(SEARCH_SHOPPER_INSIGHTS, {
    onCompleted: (data) => {
      if (data?.searchShopperInsights?.aggregations) {
        const aggregations = JSON.parse(data.searchShopperInsights.aggregations)
        const buckets = aggregations?.checkoutsOverTime?.buckets || []

        const checkoutsMap: { [key: string]: number } = {}
        buckets.forEach((bucket: { key_as_string: string; uniqueSessions: { value: number } }) => {
          checkoutsMap[bucket.key_as_string] = bucket.uniqueSessions?.value || 0
        })
        setCheckoutsOverTimeData(checkoutsMap)
      }
    }
  })

  // Time-series queries - Sessions (no paymentStatus/checkoutId)
  const [
    searchSessionsOverTime,
    { loading: sessionsOverTimeLoading, error: sessionsOverTimeError }
  ] = useLazyQuery(SEARCH_SHOPPER_INSIGHTS, {
    onCompleted: (data) => {
      if (data?.searchShopperInsights?.aggregations) {
        const aggregations = JSON.parse(data.searchShopperInsights.aggregations)
        const buckets = aggregations?.sessionsOverTime?.buckets || []

        const sessionsMap: { [key: string]: number } = {}
        buckets.forEach((bucket: { key_as_string: string; doc_count: number }) => {
          sessionsMap[bucket.key_as_string] = bucket.doc_count
        })
        setSessionsOverTimeData(sessionsMap)
      }
    }
  })

  // Merge time-series data when all queries complete
  useEffect(() => {
    const allTimestamps = new Set([
      ...Object.keys(ordersOverTimeData),
      ...Object.keys(checkoutsOverTimeData),
      ...Object.keys(sessionsOverTimeData)
    ])

    const merged: InsightsTimeSeriesData[] = Array.from(allTimestamps)
      .sort()
      .map((timestamp) => ({
        timestamp,
        orders: ordersOverTimeData[timestamp] || 0,
        checkouts: checkoutsOverTimeData[timestamp] || 0,
        sessions: sessionsOverTimeData[timestamp] || 0,
        displayLabel: formatLabel(timestamp)
      }))

    setChartData(merged)
  }, [ordersOverTimeData, checkoutsOverTimeData, sessionsOverTimeData, interval])

  const searchSuccessfulOrders = useCallback(() => {
    if (!config.insightsEnabled) return

    // Query ShopperInsights for orders by session creation time (not order date)
    const ordersQuery = esb
      .boolQuery()
      .must(
        esb
          .rangeQuery("created")
          .gte(dayjs(startDate).format("YYYY-MM-DD-HH:mm"))
          .lte(dayjs(endDate).format("YYYY-MM-DD-HH:mm"))
          .format("yyyy-MM-dd-HH:mm")
          .timeZone(timeZone)
      )
      .must(esb.existsQuery("reference"))

    if (selectedStoreGroups.length > 0) {
      ordersQuery.must(
        esb.termsQuery(
          "storeGroupId",
          selectedStoreGroups.map((sg) => sg.value)
        )
      )
    }

    if (selectedStoreMarkets.length > 0) {
      ordersQuery.must(
        esb.termsQuery(
          "countryCode",
          selectedStoreMarkets.map((sm) => sm.value)
        )
      )
    }

    // Large size to get all order sessions
    const ordersAggs = esb
      .requestBodySearch()
      .agg(esb.cardinalityAggregation("totalOrderSessions", "sessionId"))
      .agg(esb.termsAggregation("orderSessionIds", "sessionId").size(10000))
      .toJSON() as Query

    searchOrderSessions({
      variables: {
        query: JSON.stringify(ordersQuery.toJSON()),
        aggs: JSON.stringify(ordersAggs.aggs),
        from: 0,
        size: 0
      }
    })
  }, [
    config.insightsEnabled,
    startDate,
    endDate,
    selectedStoreGroups,
    selectedStoreMarkets,
    timeZone,
    searchOrderSessions
  ])

  const fetchTimeSeriesData = useCallback(() => {
    if (!config.insightsEnabled) return

    const startDateCheck = new Date(startDate)
    const startDateIsValid = startDateCheck && !isNaN(startDateCheck.getTime())
    const endDateCheck = new Date(endDate)
    const endDateIsValid = endDateCheck && !isNaN(endDateCheck.getTime())

    if (!startDateIsValid || !endDateIsValid) return

    const dateHistogramInterval = interval === "hourly" ? "1h" : "1d"

    // Orders over time query
    const ordersBoolQuery = esb
      .boolQuery()
      .must(
        esb
          .rangeQuery("date")
          .gte(dayjs(startDate).format("YYYY-MM-DD-HH:mm"))
          .lte(dayjs(endDate).format("YYYY-MM-DD-HH:mm"))
          .format("yyyy-MM-dd-HH:mm")
          .timeZone(timeZone)
      )

    if (selectedStoreGroups.length > 0) {
      ordersBoolQuery.must(
        esb.termsQuery(
          "storeGroupId",
          selectedStoreGroups.map((option) => option.value)
        )
      )
    }

    if (selectedStoreMarkets.length > 0) {
      ordersBoolQuery.must(
        esb.termsQuery(
          "countryCode",
          selectedStoreMarkets.map((option) => option.value)
        )
      )
    }

    const ordersAggs = esb
      .requestBodySearch()
      .agg(
        esb
          .dateHistogramAggregation("ordersOverTime", "date")
          .interval(dateHistogramInterval)
          .minDocCount(0)
          .extendedBounds(dayjs(startDate).valueOf(), dayjs(endDate).valueOf())
      )
      .toJSON() as Query

    // Checkouts over time query (has paymentStatus field)
    const checkoutsBoolQuery = esb
      .boolQuery()
      .must(
        esb
          .rangeQuery("created")
          .gte(dayjs(startDate).format("YYYY-MM-DD-HH:mm"))
          .lte(dayjs(endDate).format("YYYY-MM-DD-HH:mm"))
          .format("yyyy-MM-dd-HH:mm")
          .timeZone(timeZone)
      )
      .must(esb.existsQuery("paymentStatus"))

    if (selectedStoreGroups.length > 0) {
      checkoutsBoolQuery.must(
        esb.termsQuery(
          "storeGroupId",
          selectedStoreGroups.map((option) => option.value)
        )
      )
    }

    if (selectedStoreMarkets.length > 0) {
      checkoutsBoolQuery.must(
        esb.termsQuery(
          "countryCode",
          selectedStoreMarkets.map((option) => option.value)
        )
      )
    }

    const checkoutsAggs = esb
      .requestBodySearch()
      .agg(
        esb
          .dateHistogramAggregation("checkoutsOverTime", "created")
          .interval(dateHistogramInterval)
          .minDocCount(0)
          .extendedBounds(dayjs(startDate).valueOf(), dayjs(endDate).valueOf())
          .agg(esb.cardinalityAggregation("uniqueSessions", "sessionId"))
      )
      .toJSON() as Query

    // Sessions over time query (pure sessions without paymentStatus/checkoutId)
    const sessionsBoolQuery = esb
      .boolQuery()
      .must(
        esb
          .rangeQuery("created")
          .gte(dayjs(startDate).format("YYYY-MM-DD-HH:mm"))
          .lte(dayjs(endDate).format("YYYY-MM-DD-HH:mm"))
          .format("yyyy-MM-dd-HH:mm")
          .timeZone(timeZone)
      )
      .mustNot(esb.existsQuery("paymentStatus"))
      .mustNot(esb.existsQuery("checkoutId"))

    if (selectedStoreGroups.length > 0) {
      sessionsBoolQuery.must(
        esb.termsQuery(
          "storeGroupId",
          selectedStoreGroups.map((option) => option.value)
        )
      )
    }

    if (selectedStoreMarkets.length > 0) {
      sessionsBoolQuery.must(
        esb.termsQuery(
          "countryCode",
          selectedStoreMarkets.map((option) => option.value)
        )
      )
    }

    const sessionsAggs = esb
      .requestBodySearch()
      .agg(
        esb
          .dateHistogramAggregation("sessionsOverTime", "created")
          .interval(dateHistogramInterval)
          .minDocCount(0)
          .extendedBounds(dayjs(startDate).valueOf(), dayjs(endDate).valueOf())
      )
      .toJSON() as Query

    searchOrdersOverTime({
      variables: {
        from: 0,
        size: 0,
        query: JSON.stringify(ordersBoolQuery.toJSON()),
        trackTotalHits: false,
        aggs: JSON.stringify(ordersAggs.aggs)
      }
    })

    searchCheckoutsOverTime({
      variables: {
        from: 0,
        size: 0,
        query: JSON.stringify(checkoutsBoolQuery.toJSON()),
        aggs: JSON.stringify(checkoutsAggs.aggs)
      }
    })

    searchSessionsOverTime({
      variables: {
        from: 0,
        size: 0,
        query: JSON.stringify(sessionsBoolQuery.toJSON()),
        aggs: JSON.stringify(sessionsAggs.aggs)
      }
    })
  }, [
    config.insightsEnabled,
    startDate,
    endDate,
    selectedStoreGroups,
    selectedStoreMarkets,
    interval,
    timeZone
  ])

  const searchTotalCheckoutSessions = useCallback(() => {
    if (!config.insightsEnabled) return

    // Query for ALL checkout sessions (any paymentStatus)
    const allCheckoutsBoolQuery = esb
      .boolQuery()
      .must(
        esb
          .rangeQuery("created")
          .gte(startDate.replace("T", "-"))
          .lte(endDate.replace("T", "-"))
          .format("yyyy-MM-dd-HH:mm")
          .timeZone(timeZone)
      )
      .must(esb.existsQuery("paymentStatus"))

    if (selectedStoreGroups.length > 0) {
      allCheckoutsBoolQuery.must(
        esb.termsQuery(
          "storeGroupId",
          selectedStoreGroups.map((sg) => sg.value)
        )
      )
    }

    if (selectedStoreMarkets.length > 0) {
      allCheckoutsBoolQuery.must(
        esb.termsQuery(
          "countryCode",
          selectedStoreMarkets.map((sm) => sm.value)
        )
      )
    }

    const allCheckoutsAggs = esb
      .requestBodySearch()
      .agg(esb.cardinalityAggregation("totalUniqueCheckoutSessions", "sessionId"))
      .toJSON() as Query

    searchAllCheckoutSessions({
      variables: {
        query: JSON.stringify(allCheckoutsBoolQuery.toJSON()),
        aggs: JSON.stringify(allCheckoutsAggs.aggs),
        from: 0,
        size: 0
      }
    })
  }, [
    config.insightsEnabled,
    startDate,
    endDate,
    selectedStoreGroups,
    selectedStoreMarkets,
    timeZone,
    searchAllCheckoutSessions
  ])

  const searchCheckouts = useCallback(
    (searchTerm?: string) => {
      if (!config.insightsEnabled) return

      const boolQuery = esb
        .boolQuery()
        .must(
          esb
            .rangeQuery("created")
            .gte(startDate.replace("T", "-"))
            .lte(endDate.replace("T", "-"))
            .format("yyyy-MM-dd-HH:mm")
            .timeZone(timeZone)
        )

      if (selectedStoreGroups.length > 0) {
        boolQuery.must(
          esb.termsQuery(
            "storeGroupId",
            selectedStoreGroups.map((sg) => sg.value)
          )
        )
      }

      if (selectedStoreMarkets.length > 0) {
        boolQuery.must(
          esb.termsQuery(
            "countryCode",
            selectedStoreMarkets.map((sm) => sm.value)
          )
        )
      }

      if (selectedReasons.length > 0) {
        const reasonQuery = esb.boolQuery()
        selectedReasons.forEach((reason) => {
          reasonQuery.should(esb.termQuery("paymentStatus.current", reason.value))
        })
        reasonQuery.minimumShouldMatch(1)
        boolQuery.must(reasonQuery)
      }

      // Filter by payment status if specific reasons are selected
      // Otherwise show all checkouts (will be filtered by orderSessionIds later)
      if (searchTerm && searchTerm.trim()) {
        boolQuery
          .should([
            esb.termQuery("orderReference", searchTerm.trim()),
            esb.wildcardQuery("orderReference", `*${searchTerm.trim()}*`),
            esb.termQuery("shippingAddress.email", searchTerm.trim()),
            esb.wildcardQuery("shippingAddress.email", `*${searchTerm.trim()}*`)
          ])
          .minimumShouldMatch(1)
      }

      // All checkouts must have a paymentStatus field
      boolQuery.must(esb.existsQuery("paymentStatus"))

      // Simpler aggregations without the large topHits aggregation
      // We'll fetch documents directly and deduplicate in JavaScript
      const aggs = esb
        .requestBodySearch()
        .agg(esb.cardinalityAggregation("totalUniqueCheckoutSessions", "sessionId"))
        .agg(
          esb
            .filterAggregation(
              "rejectedSessions",
              esb.termQuery("paymentStatus.current", "REJECTED")
            )
            .agg(esb.cardinalityAggregation("uniqueRejectedSessions", "sessionId"))
        )
        .agg(
          esb
            .filterAggregation("startedSessions", esb.termQuery("paymentStatus.current", "STARTED"))
            .agg(esb.cardinalityAggregation("uniqueStartedSessions", "sessionId"))
        )
        .agg(
          esb
            .filterAggregation("onHoldSessions", esb.termQuery("paymentStatus.current", "ON_HOLD"))
            .agg(esb.cardinalityAggregation("uniqueOnHoldSessions", "sessionId"))
        )
        .agg(
          esb
            .filterAggregation("pendingSessions", esb.termQuery("paymentStatus.current", "PENDING"))
            .agg(esb.cardinalityAggregation("uniquePendingSessions", "sessionId"))
        )
        .agg(
          esb
            .termsAggregation("storeGroups", "storeGroupId")
            .size(500)
            .missing("Unknown")
            .order("latestCheckout", "desc")
            .agg(esb.cardinalityAggregation("uniqueSessions", "sessionId"))
            .agg(esb.maxAggregation("latestCheckout", "created"))
        )
        .agg(
          esb
            .termsAggregation("countryBreakdown", "countryCode")
            .size(500)
            .missing("Unknown")
            .order("latestCheckout", "desc")
            .agg(esb.cardinalityAggregation("uniqueSessions", "sessionId"))
            .agg(esb.maxAggregation("latestCheckout", "created"))
        )
        .agg(
          esb
            .termsAggregation("paymentProviders", "providers.payment.providerName")
            .size(500)
            .missing("Unknown")
            .order("latestCheckout", "desc")
            .agg(esb.cardinalityAggregation("uniqueSessions", "sessionId"))
            .agg(esb.maxAggregation("latestCheckout", "created"))
        ) as Query

      // Query 1: Fetch checkout documents for table display (with data)
      // Fetch 2x numberOfHits to account for deduplication
      searchShopperInsights({
        variables: {
          query: JSON.stringify(boolQuery.toJSON()),
          from: 0,
          size: parseInt(numberOfHits) * 2
        }
      })

      // Query 2: Fetch aggregations/counts only (no document data)
      searchCheckoutCounts({
        variables: {
          query: JSON.stringify(boolQuery.toJSON()),
          aggs: JSON.stringify(aggs.toJSON().aggs),
          from: 0,
          size: 0
        }
      })

      searchSuccessfulOrders()
      searchTotalCheckoutSessions()
    },
    [
      config.insightsEnabled,
      startDate,
      endDate,
      selectedStoreGroups,
      selectedStoreMarkets,
      selectedReasons,
      numberOfHits,
      searchShopperInsights,
      searchCheckoutCounts,
      searchSuccessfulOrders,
      searchTotalCheckoutSessions,
      timeZone
    ]
  )

  const handleRefresh = () => {
    searchCheckouts()
  }

  // Fetch aggregated data
  useEffect(() => {
    if (config.insightsEnabled) {
      searchCheckouts()
    }
  }, [config.insightsEnabled, searchCheckouts])

  // Fetch time-series data
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (config.insightsEnabled) {
        fetchTimeSeriesData()
      }
    }, 300)

    return () => clearTimeout(delayDebounceFn)
  }, [
    config.insightsEnabled,
    startDate,
    endDate,
    selectedStoreGroups,
    selectedStoreMarkets,
    interval
  ])

  return {
    // Aggregated data
    pendingCheckouts,
    aggregatedData,
    ordersData,
    totalCheckoutSessions,
    loading: loading || countsLoading || orderSessionsLoading || allCheckoutSessionsLoading,
    error,
    searchCheckouts,
    handleRefresh,
    // Time-series data
    chartData,
    chartLoading: ordersOverTimeLoading || checkoutsOverTimeLoading || sessionsOverTimeLoading,
    chartError: ordersOverTimeError || checkoutsOverTimeError || sessionsOverTimeError,
    refetchChartData: fetchTimeSeriesData
  }
}
